import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { updateOffer } from 'modules/shared-menu/api';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import { OptionBinding } from './OptionBinding/OptionBinding';
import { Offer, OptionBinding as OptionBindingT } from '../../types';

type OptionBindingsListProps = {
  canDrag: boolean;
  offer: Offer;
};

export const OptionBindingsList = ({ canDrag, offer }: OptionBindingsListProps) => {
  const { selection, updateSelectionData } = useSelectionData();
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));
  const [localOptionBindings, setLocalOptionBindings] = useState<null | OptionBindingT[]>();
  const [draggingOptionBinding, setDraggingOptionBinding] = useState<null | OptionBindingT>(null);
  const optionBindings = useMemo(() => offer.option_bindings ?? [], [offer]);
  const displayOptionBindings = useMemo(
    () => localOptionBindings || optionBindings,
    [localOptionBindings, optionBindings],
  );

  const { mutateAsync: executeUpdateOffer } = useMutation(
    (updatePayload: any) => updateOffer(selection.id, offer.id, updatePayload),
    {
      onSuccess: updatedOffer => {
        updateSelectionData({ actionType: 'updateOffer', payload: updatedOffer });
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const reset = useCallback(() => {
    setLocalOptionBindings(null);
    setDraggingOptionBinding(null);
  }, []);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setLocalOptionBindings(optionBindings);
      setDraggingOptionBinding(optionBindings.find(optionBinding => optionBinding.id === active.id) ?? null);
    },
    [optionBindings],
  );

  const handleDragEnd = useCallback(
    async ({ over, active }: DragEndEvent) => {
      try {
        if (!over || active?.id === over.id) {
          return;
        }
        const oldIndex = optionBindings.findIndex(option => option.id === active.id);
        const newIndex = optionBindings.findIndex(option => option.id === over.id);
        const updatedOptionBindings = arrayMove(optionBindings, oldIndex, newIndex);
        setLocalOptionBindings(updatedOptionBindings);
        const { id, version, scope, ...updatePayload }: Offer = {
          ...offer,
          option_bindings: updatedOptionBindings,
        };
        await executeUpdateOffer(updatePayload);
      } finally {
        reset();
      }
    },
    [executeUpdateOffer, optionBindings, offer, reset],
  );

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      collisionDetection={closestCenter}
    >
      <SortableContext
        strategy={verticalListSortingStrategy}
        items={displayOptionBindings.map(({ id }) => id)}
        disabled={!canDrag}
      >
        {displayOptionBindings.map(optionBinding => (
          <OptionBinding
            key={optionBinding.id}
            draggable={canDrag}
            offer={offer}
            optionBinding={optionBinding}
            primaryLanguage={selection.primary_language}
          />
        ))}
      </SortableContext>

      {createPortal(
        <DragOverlay
          dropAnimation={{
            duration: 300,
          }}
        >
          {draggingOptionBinding && (
            <OptionBinding
              offer={offer}
              draggable
              optionBinding={draggingOptionBinding}
              primaryLanguage={selection.primary_language}
            />
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
